import React, { useState } from "react";
import { Drawer,
  Row,
  Col,
  Card,
  Empty,
  message,
  Button,
  Modal,
  Select,
} from "antd";
import { ClientMessage, SupportMessage } from "../../../elements";
import { useUser } from "../../../../../providers/user";
import { useQuery, useMutation } from "@apollo/client";
import { GET_USERS, UPDATE_TICKET } from "./requests";
import { typesUsers } from "../../../../../utils/constants";
import TitleSubtitle from "../../../../../components/titleSubtitle";

const { Option } = Select;

type HistoryItem = {
  id: string;
  message: string;
  sender: "CLIENT" | "SUPPORT";
  createdAt: string;
};

type Props = {
  showModal: boolean;
  setShowModal: (value: boolean) => void;
  history: HistoryItem[] | null;
  userSupport: any | null;
  client: any | null;
  ticketId: string | null;
  refetch: () => Promise<any>;
};

const TicketDetailDrawer: React.FC<Props> = ({ showModal, setShowModal, history, userSupport, client, ticketId, refetch }) => {
  const { user } = useUser();
  const [isAssignModalOpen, setAssignModalOpen] = useState(false);
  const [isSelfAssignModalOpen, setSelfAssignModalOpen] = useState(false);
  const [selectedSupportId, setSelectedSupportId] = useState<string | null>(null);
  const [searchName, setSearchName] = useState("");

  const [updateTicket] = useMutation(UPDATE_TICKET);

  const { data, loading: loadingUsers } = useQuery(GET_USERS, {
    variables: {
      params: { page: 1,
        pageSize: 15 },
      ...(searchName ? { name: searchName } : {}),
      type: typesUsers.support.value,
    },
  });

  const supportUsers = data?.users?.results ?? [];

  const onClose = ()=>{
    setShowModal(false);
    setSelectedSupportId(null);
    setSearchName("");
  }

  const handleAssignSupport = async () => {
    if (!selectedSupportId) return;
    try {
      await updateTicket({
        variables: {
          id: ticketId,
          ticket: {
            user: selectedSupportId,
          },
        },
      });
      await refetch();
      message.success("Usuario asignado con éxito");
      setAssignModalOpen(false);
      onClose();
    } catch (err) {
      console.log(err);
      message.error("Error al asignar usuario");
    }
  };

  const handleSelfAssign = async () => {
    try {
      await updateTicket({
        variables: {
          id: ticketId,
          ticket: {
            user: user?.id,
          },
        },
      });
      await refetch();
      message.success("Te asignaste el ticket correctamente");
      setSelfAssignModalOpen(false);
      onClose();
    } catch (err) {
      console.log(err);
      message.error("Error al asignarte el ticket");
    }
  };

  const renderSupportInfo = () => {
    if (userSupport) {
      return (
        <TitleSubtitle
          title="Usuario asignado"
          subtitle={`${userSupport.firstName} ${userSupport.lastName}`}
        />
      );
    }

    if (user?.type === typesUsers.admin.value) {
      return (
        <>
          <Button type="primary" onClick={() => setAssignModalOpen(true)}>
            Asignar usuario
          </Button>
          <Modal
            title="Asignar usuario de soporte"
            open={isAssignModalOpen}
            onCancel={() => setAssignModalOpen(false)}
            onOk={handleAssignSupport}
            okText="Asignar"
            cancelText="Cancelar"
            destroyOnClose
          >
            <Select
              loading={loadingUsers}
              style={{ width: "100%" }}
              placeholder="Selecciona un usuario de soporte"
              onChange={(value) => setSelectedSupportId(value)}
              onSearch={(value) => setSearchName(value)}
              filterOption={false}
              showSearch
              allowClear
            >
              {supportUsers.map((support) => (
                <Option key={support.id} value={support.id}>
                  {support.firstName} {support.lastName}
                </Option>
              ))}
            </Select>
          </Modal>
        </>
      );
    }

    if (user?.type === typesUsers.support.value) {
      return (
        <>
          <Button type="primary" onClick={() => setSelfAssignModalOpen(true)}>
            Asignarse este ticket
          </Button>
          <Modal
            title="Confirmar asignación"
            open={isSelfAssignModalOpen}
            onCancel={() => setSelfAssignModalOpen(false)}
            onOk={handleSelfAssign}
            okText="Sí, asignarme"
            cancelText="Cancelar"
          >
            <p>¿Estás seguro que deseas asignarte este ticket?</p>
          </Modal>
        </>
      );
    }

    return null;
  };

  return (
    <Drawer
      title={`Historial del Ticket del cliente: ${client?.user?.firstName} ${client?.user?.lastName}`}
      placement="right"
      width={"50%"}
      onClose={onClose}
      open={showModal}
    >
      <Row justify="space-between" style={{ marginBottom: 16 }}>
        {renderSupportInfo()}
      </Row>

      <Row style={{ justifyContent: "space-between",
        marginTop: 5 }}>
        <Col style={{ width: "100%" }}>
          <Card
            style={{
              borderRadius: 20,
              overflow: "auto",
            }}
          >
            <Col
              style={{
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "relative",
              }}
            >
              <Row
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {history?.length === 0 ? (
                  <Empty description="No hay mensajes en este historial" />
                ) : (
                  history?.map((message) => (
                    <div
                      key={message.id + message.createdAt}
                      style={{
                        display: "flex",
                        justifyContent:
                              message.sender === "SUPPORT" ? "flex-start" : "flex-end",
                        width: "100%",
                        padding: "10px 0",
                        flexDirection: "column",
                        alignItems: message.sender === "SUPPORT" ? "flex-start" : "flex-end",
                      }}
                    >
                      {message.sender === "SUPPORT" ? (
                        <SupportMessage>
                          {message.message}
                          <div style={{ fontSize: 12,
                            color: "#f0f0f0",
                            marginTop: 5 }}>
                            {new Date(message.createdAt).toLocaleString("es-MX", {
                              dateStyle: "short",
                              timeStyle: "short",
                            })}
                          </div>
                        </SupportMessage>
                      ) : (
                        <ClientMessage>
                          {message.message}
                          <div style={{ fontSize: 12,
                            color: "#333",
                            marginTop: 5 }}>
                            {new Date(message.createdAt).toLocaleString("es-MX", {
                              dateStyle: "short",
                              timeStyle: "short",
                            })}
                          </div>
                        </ClientMessage>
                      )}
                    </div>
                  ))
                )}
              </Row>
            </Col>
          </Card>
        </Col>
      </Row>
    </Drawer>
  );
};

export default TicketDetailDrawer;
